import { useTranslation } from '@thoughtspot/i18n';
import cx from 'classnames';
import _ from 'lodash';
import React, { useEffect, useRef, useState } from 'react';
import { generateUUID, getOnEnter, getUniqueKey } from '../_utils';
import { Input } from '../input';
import { defaultColors, defaultExtendedColors, defaultFullColours, getFullColours, getPrimaryColors, getPrimaryExtendedColors, getSecondaryColors, getSecondaryExtendedColors, grayscaleColors, whiteColor, } from './color-constant';
import styles from './colorpicker.module.scss';
export var PICKER_VIEW;
(function (PICKER_VIEW) {
    PICKER_VIEW["FULL"] = "F";
    PICKER_VIEW["LIMITED"] = "L";
    PICKER_VIEW["EXTENDED"] = "E";
})(PICKER_VIEW || (PICKER_VIEW = {}));
const renderColors = (colorsMap, selectColor, selectedColor, disabled, colorAriaLabelText) => (React.createElement("div", { className: styles.colorScale }, colorsMap.map((colors) => (React.createElement("div", { className: styles.colorBar, key: getUniqueKey(colors) }, colors.map((color) => (React.createElement("div", { className: cx(styles.colorScaleBlock, {
        [styles.pickedColor]: color === selectedColor,
        [styles.whiteColor]: color === whiteColor,
        [styles.disabled]: disabled,
    }), key: generateUUID(), style: { backgroundColor: color }, onClick: () => !disabled && selectColor(color), tabIndex: 0, role: "option", "aria-selected": color === selectedColor, "aria-disabled": disabled, "aria-label": `${colorAriaLabelText || ''} ${color}`, onKeyDown: getOnEnter(() => !disabled && selectColor(color)), "data-testid": `color-picker-button-${color}` }))))))));
const hexColorRegExp = /^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/i;
export const ColorPickerMenu = ({ defaultValue = '', viewType = PICKER_VIEW.LIMITED, colors = [], onChange = _.noop, className = '', hexColorTitle = '', autoLabel = '', hexColorPlaceHolder = '', isVisible = false, setIsVisible = _.noop, targetRef, menuRef, showEmptyColor = false, disabled = false, ...restProps }) => {
    const [currentColor, setCurrentColor] = useState(defaultValue.toUpperCase());
    const [isCustomHexColorValid, setIsCustomHexColorValid] = useState(true);
    const { t } = useTranslation();
    const colorPickerMenuRef = useRef(null);
    const selectColor = (color, closeColorPickerMenu = true) => {
        setCurrentColor(color);
        if (isVisible && closeColorPickerMenu) {
            setIsVisible(false);
        }
        onChange(color);
    };
    const getDefaultPrimaryColors = () => {
        switch (viewType) {
            case PICKER_VIEW.LIMITED:
                return getPrimaryColors(defaultColors);
            case PICKER_VIEW.EXTENDED:
                return getPrimaryExtendedColors(defaultExtendedColors);
            default:
                return getFullColours(defaultFullColours);
        }
    };
    const getDefaultSecondaryColors = () => {
        switch (viewType) {
            case PICKER_VIEW.EXTENDED:
                return getSecondaryExtendedColors(defaultExtendedColors);
            default:
                return getSecondaryColors(defaultColors);
        }
    };
    const getCustomPrimaryColors = () => {
        switch (viewType) {
            case PICKER_VIEW.EXTENDED:
                return getPrimaryExtendedColors(colors);
            default:
                return getPrimaryColors(colors);
        }
    };
    const getCustomSecondaryColors = () => {
        switch (viewType) {
            case PICKER_VIEW.EXTENDED:
                return getSecondaryExtendedColors(colors);
            default:
                return getSecondaryColors(colors);
        }
    };
    const allColors = {
        primary: colors.length
            ? getCustomPrimaryColors()
            : getDefaultPrimaryColors(),
        secondary: colors.length
            ? getCustomSecondaryColors()
            : getDefaultSecondaryColors(),
    };
    const handleCustomHexColorChange = (event, customHexColor) => {
        event.stopPropagation();
        if (hexColorRegExp.test(customHexColor)) {
            setCurrentColor(customHexColor);
            if (!isCustomHexColorValid) {
                setIsCustomHexColorValid(true);
            }
        }
        else if (isCustomHexColorValid) {
            setIsCustomHexColorValid(false);
        }
    };
    const onCustomHexColorEnter = (event) => {
        event.stopPropagation();
        if (isCustomHexColorValid) {
            selectColor(currentColor);
        }
    };
    const handleDocumentEvent = (event) => {
        if (menuRef.current && menuRef.current.contains(event.target)) {
            // click handler inside select menu options
            return;
        }
        if (isVisible && isCustomHexColorValid) {
            onChange(currentColor);
        }
    };
    useEffect(() => {
        document.addEventListener('mousedown', handleDocumentEvent);
        return () => {
            document.removeEventListener('mousedown', handleDocumentEvent);
        };
    }, [currentColor, handleDocumentEvent]);
    const selectEmptyColor = () => selectColor('');
    return (React.createElement("div", { className: cx(styles.content, className), ref: colorPickerMenuRef, ...restProps },
        React.createElement("div", { className: styles.colorPickerContent },
            React.createElement("div", { className: styles.primaryColor }, renderColors(allColors.primary, selectColor, currentColor, disabled, t('radiantReact.colorPicker.ariaLabel'))),
            (viewType === PICKER_VIEW.LIMITED ||
                viewType === PICKER_VIEW.EXTENDED) && (React.createElement("div", { className: styles.secondaryColor }, renderColors(allColors.secondary, selectColor, currentColor, disabled, t('radiantReact.colorPicker.ariaLabel')))),
            viewType === PICKER_VIEW.EXTENDED && (React.createElement("div", { className: styles.grayscaleRow }, renderColors(grayscaleColors, selectColor, currentColor, disabled, t('radiantReact.colorPicker.ariaLabel')))),
            showEmptyColor && (React.createElement("div", { className: cx(styles.emptyColorBlock, {
                    [styles.disabled]: disabled,
                }), onClick: () => !disabled && selectEmptyColor(), tabIndex: disabled ? -1 : 0, role: "button", "aria-disabled": disabled, "aria-label": t('radiantReact.noColor'), onKeyDown: getOnEnter(() => !disabled && selectEmptyColor()) })),
            React.createElement("div", null,
                React.createElement("div", { className: styles.hexColor }, hexColorTitle),
                React.createElement("div", { className: styles.hexInputContainer },
                    React.createElement(Input.Text, { ariaLabel: hexColorPlaceHolder, className: styles.hexInputField, placeholder: hexColorPlaceHolder, value: currentColor, onChange: (event, customHexColor) => handleCustomHexColorChange(event, customHexColor), hasError: !isCustomHexColorValid, onEnter: onCustomHexColorEnter, helperText: !isCustomHexColorValid ? 'Invalid Color' : '', disabled: disabled }),
                    React.createElement("div", { className: styles.autoLabelWrapper },
                        React.createElement("div", { className: styles.selectedColor, style: { backgroundColor: currentColor } }),
                        React.createElement("div", { className: styles.autoLabel }, autoLabel)))))));
};
//# sourceMappingURL=colorpicker-menu.js.map